import React, { useEffect, useState, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  collectionGroup,
  onSnapshot,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { useAppData } from '../context/AppDataContext';
import '../styles/AdminDashboard.css';
import SearchBar from '../components/SearchBar';

// Shared Facebook ad constants- edit src/constants/facebookAdConstants.js to add templates or ad types
import { VERBIAGE_TEMPLATES, AVAILABLE_AD_TYPES as CONSTANTS_AD_TYPES, toBoldUnicode } from '../constants/facebookAdConstants';


function DashboardCard({ title, value, onClick }) {
  return (
    <div className="bento-card" onClick={onClick}>
      <h3>{title}</h3>
      <p>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { orders: cachedOrders, clients: cachedClients, facebookEvents: cachedFacebookEvents, loaded, refresh } = useAppData();
  const [username, setUsername] = useState('');
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [announcement, setAnnouncement] = useState('');
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [stats, setStats] = useState({
    openOrders: 0,
    approvalsClient: 0,
    approvalsAdmin: 0,
    insurancePending: 0,
    prizeboardPending: 0,
    messages: 0,
    incompleteArt: 0,
    currentFacebookEvents: 0,
    artDueSoon: 0,
  });
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ orders: [], clients: [] });
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);

  // Insurance notes
  const [insuranceNote, setInsuranceNote] = useState('');
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [savedNotes, setSavedNotes] = useState([]);

  // Sample Vault state
  const [showSampleVaultModal, setShowSampleVaultModal] = useState(false);
  const [sampleVaultJobs, setSampleVaultJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [newJobName, setNewJobName] = useState('');
  const [sampleFile, setSampleFile] = useState(null);
  const [sampleFileName, setSampleFileName] = useState('');
  const [vaultModalMode, setVaultModalMode] = useState('full');

  // Activity Feed and Recent Orders state
  const [activities, setActivities] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  // Facebook Ads carousel state
  // each entry: { url, year, month, adType }
  const [facebookAds, setFacebookAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [adLoading, setAdLoading] = useState(true);
  const adIntervalRef = useRef(null);

  // Download Ad modal state
  const [showDownloadAdModal, setShowDownloadAdModal] = useState(false);
  const [downloadAdInfo, setDownloadAdInfo] = useState(null); // { year, month, adType }
  const [downloadSelectedClients, setDownloadSelectedClients] = useState(new Set());
  const [downloadClientSearch, setDownloadClientSearch] = useState('');
  const [downloadingAd, setDownloadingAd] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Search Ads modal state
  const [showSearchAdsModal, setShowSearchAdsModal] = useState(false);
  const [adSearchQuery, setAdSearchQuery] = useState('');
  const [searchSelectedAd, setSearchSelectedAd] = useState(null); // { year, month, adType, displayName }

  // Verbiage modal state
  const [showVerbiageModal, setShowVerbiageModal] = useState(false);
  const [verbiageTemplateSearch, setVerbiageTemplateSearch] = useState('');
  const [selectedVerbiageTemplate, setSelectedVerbiageTemplate] = useState(null);
  const [verbiageClientSearch, setVerbiageClientSearch] = useState('');
  const [selectedVerbiageClientId, setSelectedVerbiageClientId] = useState('');
  const [generatedVerbiage, setGeneratedVerbiage] = useState('');
  const [generatingVerbiage, setGeneratingVerbiage] = useState(false);
  const [verbiageCopied, setVerbiageCopied] = useState(false);

  // Helper function to calculate art due date
  const getArtDueDate = (drops) => {
    const firstStart = drops?.[0]?.startDate;
    if (!firstStart) return null;
    const date = firstStart.toDate ? firstStart.toDate() : new Date(firstStart);
    const artDueDate = new Date(date);
    artDueDate.setDate(artDueDate.getDate() - 12);
    return artDueDate;
  };

  // Helper function to download file
  const downloadFile = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  // eslint-disable-next-line
  const isArtDueSoon = (order) => {
    if (order.status !== 'Incomplete - Need Art') return false;
    const artDueDate = getArtDueDate(order.drops);
    if (!artDueDate) return false;
    const today = new Date();
    const threeDaysFromNow = new Date(today);
    threeDaysFromNow.setDate(today.getDate() + 3);
    return artDueDate <= threeDaysFromNow;
  };

  // Load username
  useEffect(() => {
    async function loadUsername() {
      const auth = getAuth();
      const u = auth.currentUser;
      if (!u) return;
      const userDocRef = doc(db, 'users', u.uid);
      try {
        const snap = await getDoc(userDocRef);
        if (snap.exists()) {
          const data = snap.data();
          setUsername(data.name || '');
        }
      } catch (err) {
        console.error('Error loading user name:', err);
      }
    }
    loadUsername();
  }, []);

  // Load saved insurance notes
  useEffect(() => {
    async function loadSavedNotes() {
      try {
        const notesSnap = await getDocs(collection(db, 'insuranceNotes'));
        const notes = notesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSavedNotes(notes);
      } catch (err) {
        console.error('Error loading saved notes:', err);
      }
    }
    loadSavedNotes();
  }, []);

  // Load Sample Vault jobs
  useEffect(() => {
    async function loadSampleVaultJobs() {
      try {
        const jobsSnap = await getDocs(collection(db, 'sampleVaultJobs'));
        const jobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        jobs.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
        setSampleVaultJobs(jobs);
      } catch (err) {
        console.error('Error loading sample vault jobs:', err);
      }
    }
    loadSampleVaultJobs();
  }, []);

  // Fetch Facebook Ads- one image per adType, walking back months until we have 20 ads
  useEffect(() => {
    async function fetchFacebookAds() {
      try {
        setAdLoading(true);

        const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const now = new Date();

        // Build a list of up to 6 months going backwards from current month
        const monthsToFetch = [];
        for (let i = 0; i < 6; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          monthsToFetch.push({ year: d.getFullYear().toString(), month: MONTHS[d.getMonth()] });
        }

        const adItems = []; // { url, year, month, adType }

        for (const { year, month } of monthsToFetch) {
          if (adItems.length >= 20) break;
          try {
            const monthRef = ref(storage, `facebook-ad-templates/${year}/${month}`);
            const adTypesResult = await listAll(monthRef);
            for (const adTypeRef of adTypesResult.prefixes) {
              if (adItems.length >= 20) break;
              const filesResult = await listAll(adTypeRef);
              const imageFile = filesResult.items.find(item => {
                const n = item.name.toLowerCase();
                return n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.png');
              });
              if (imageFile) {
                const url = await getDownloadURL(imageFile);
                adItems.push({ url, year, month, adType: adTypeRef.name });
              }
            }
          } catch (e) {
            // month folder may not exist yet- skip silently
          }
        }

        setFacebookAds(adItems);
        setCurrentAdIndex(0);
      } catch (err) {
        console.error('Error fetching Facebook ads:', err);
      } finally {
        setAdLoading(false);
      }
    }
    fetchFacebookAds();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (facebookAds.length < 2) return;
    adIntervalRef.current = setInterval(() => {
      setCurrentAdIndex(prev => (prev + 1) % facebookAds.length);
    }, 4000);
    return () => clearInterval(adIntervalRef.current);
  }, [facebookAds]);

  // Shared: generate and trigger download for one client + ad combo
  const generateAndDownloadSingleAd = async (ad, client) => {
    const canvas = document.createElement('canvas');
    canvas.width = 2500; canvas.height = 2500;
    const ctx = canvas.getContext('2d');

    const templateRef = ref(storage, `facebook-ad-templates/${ad.year}/${ad.month}/${ad.adType}/${client.manufacturer}.jpg`);
    const templateUrl = await getDownloadURL(templateRef);

    await new Promise((resolve, reject) => {
      const bg = new Image();
      bg.crossOrigin = 'anonymous';
      bg.onload = async () => {
        ctx.drawImage(bg, 0, 0, 2500, 2500);

        // Load placement.json
        let logoPlacement = { box: { x: 500, y: 300, width: 1000, height: 500 }, rotation: 0, objectFit: 'contain', padding: 0, horizontalAlign: 'center', verticalAlign: 'center' };
        try {
          const pRef = ref(storage, `facebook-ad-templates/${ad.year}/${ad.month}/${ad.adType}/placement.json`);
          const pUrl = await getDownloadURL(pRef);
          const resp = await fetch(pUrl, { cache: 'no-store' });
          if (resp.ok) {
            const raw = await resp.json();
            if (raw.logo || raw.address) logoPlacement = raw.logo || logoPlacement;
            else if (raw.box || raw.x !== undefined) logoPlacement = { box: raw.box || { x: Number(raw.x??0), y: Number(raw.y??0), width: Number(raw.maxWidth??300), height: Number(raw.maxHeight??120) }, rotation: Number(raw.rotation??0), objectFit: raw.objectFit||'contain', padding: Number(raw.padding??0), horizontalAlign: raw.horizontalAlign||'center', verticalAlign: raw.verticalAlign||'center' };
          }
        } catch (_) {}

        // Draw logo
        if (client.blackLogo && logoPlacement) {
          await new Promise(res => {
            const logo = new Image();
            logo.crossOrigin = 'anonymous';
            logo.onload = () => {
              const { box, objectFit = 'contain', padding = 0, horizontalAlign = 'center', verticalAlign = 'center' } = logoPlacement;
              const avW = box.width - padding * 2, avH = box.height - padding * 2;
              const ratio = logo.width / logo.height;
              let lw, lh;
              if (objectFit === 'contain') { if (ratio > avW / avH) { lw = avW; lh = avW / ratio; } else { lh = avH; lw = avH * ratio; } } else { lw = avW; lh = avH; }
              const lx = box.x + padding + (horizontalAlign === 'center' ? (avW - lw) / 2 : horizontalAlign === 'right' ? avW - lw : 0);
              const ly = box.y + padding + (verticalAlign === 'center' ? (avH - lh) / 2 : verticalAlign === 'bottom' ? avH - lh : 0);
              if (logoPlacement.rotation) { ctx.save(); ctx.translate(lx + lw / 2, ly + lh / 2); ctx.rotate(logoPlacement.rotation * Math.PI / 180); ctx.drawImage(logo, -lw / 2, -lh / 2, lw, lh); ctx.restore(); }
              else ctx.drawImage(logo, lx, ly, lw, lh);
              res();
            };
            logo.onerror = res;
            logo.src = client.blackLogo;
          });
        }

        const link = document.createElement('a');
        link.download = `${client.name}_${ad.adType}_${ad.month}${ad.year}.jpg`;
        link.href = canvas.toDataURL('image/jpeg', 0.95);
        link.click();
        resolve();
      };
      bg.onerror = reject;
      bg.src = templateUrl;
    });
  };

  const resumeCarousel = () => {
    if (facebookAds.length > 1) adIntervalRef.current = setInterval(() => setCurrentAdIndex(p => (p + 1) % facebookAds.length), 4000);
  };

  const closeDownloadModal = () => {
    setShowDownloadAdModal(false);
    setDownloadAdInfo(null);
    setDownloadSelectedClients(new Set());
    setDownloadClientSearch('');
    setDownloadProgress({ current: 0, total: 0 });
    resumeCarousel();
  };

  const closeSearchAdsModal = () => {
    setShowSearchAdsModal(false);
    setAdSearchQuery('');
    setSearchSelectedAd(null);
    setDownloadSelectedClients(new Set());
    setDownloadClientSearch('');
    setDownloadProgress({ current: 0, total: 0 });
    resumeCarousel();
  };

  const closeVerbiageModal = () => {
    setShowVerbiageModal(false);
    setVerbiageTemplateSearch('');
    setSelectedVerbiageTemplate(null);
    setVerbiageClientSearch('');
    setSelectedVerbiageClientId('');
    setGeneratedVerbiage('');
    setVerbiageCopied(false);
    resumeCarousel();
  };

  const generateVerbiageForClient = async (templateName, clientId) => {
    if (!templateName || !clientId) return;
    const client = clients.find(c => c.id === clientId);
    if (!client) return;

    setGeneratingVerbiage(true);
    setGeneratedVerbiage('');
    try {
      let templateContent;
      if (client.customTemplates && client.customTemplates[templateName]) {
        templateContent = client.customTemplates[templateName];
      } else {
        const templateUrl = VERBIAGE_TEMPLATES[templateName];
        if (!templateUrl) throw new Error(`Template not found: ${templateName}`);
        const response = await fetch(templateUrl);
        if (!response.ok) throw new Error(`Failed to load template: ${templateName}`);
        templateContent = await response.text();
      }

      const dealershipName = client.name?.trim() || '';
      const street = client.street1?.trim() || '';
      const cityStateZip = `${client.city?.trim() || ''}, ${client.state?.trim() || ''} ${client.zip?.trim() || ''}`.trim();
      const disclaimer = client.disclaimerLink?.trim() || 'No disclaimer provided';

      const boldName = toBoldUnicode(dealershipName.toUpperCase());
      const boldStreet = toBoldUnicode(street.toUpperCase());
      const boldCityStateZip = toBoldUnicode(cityStateZip.toUpperCase());

      const filled = templateContent
        .replace(/<<DEALERSHIP_NAME>>/g, boldName)
        .replace(/<<Address>>/g, boldStreet)
        .replace(/<<Address2>>/g, boldCityStateZip)
        .replace(/<<Disclaimer>>/g, disclaimer);

      setGeneratedVerbiage(filled);
    } catch (err) {
      console.error('Verbiage error:', err);
      alert(`Error generating verbiage: ${err.message}`);
    } finally {
      setGeneratingVerbiage(false);
    }
  };

  // Download ad for all selected clients
  const handleDownloadForClients = async (ad) => {
    if (!ad) { alert('No ad selected'); return; }
    if (downloadSelectedClients.size === 0) { alert('Select at least one dealership'); return; }

    const selectedList = clients.filter(c => downloadSelectedClients.has(c.id) && c.manufacturer);
    if (selectedList.length === 0) { alert('Selected clients have no manufacturer set'); return; }

    setDownloadingAd(true);
    setDownloadProgress({ current: 0, total: selectedList.length });
    const skipped = [];

    for (let i = 0; i < selectedList.length; i++) {
      const client = selectedList[i];
      setDownloadProgress({ current: i + 1, total: selectedList.length });
      try {
        await generateAndDownloadSingleAd(ad, client);
        if (i < selectedList.length - 1) await new Promise(r => setTimeout(r, 300));
      } catch (err) {
        console.warn(`Skipped ${client.name}:`, err.message);
        skipped.push(client.name);
      }
    }

    setDownloadingAd(false);
    setDownloadProgress({ current: 0, total: 0 });

    if (skipped.length > 0) {
      alert(`Done! ${selectedList.length - skipped.length} downloaded.\n\nSkipped (no template for their manufacturer):\n${skipped.join('\n')}`);
    }

    closeDownloadModal();
    closeSearchAdsModal();
  };

  // All available ads flat list- derived from the shared CONSTANTS_AD_TYPES
  const ALL_AD_TYPES = React.useMemo(() => {
    const list = [];
    const MONTH_IDX = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    Object.entries(CONSTANTS_AD_TYPES).forEach(([year, yearData]) => {
      Object.entries(yearData).forEach(([month, adTypes]) => {
        adTypes.forEach(adType => {
          list.push({
            adType, year, month,
            displayName: adType.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([a-zA-Z])(\d)/g, '$1 $2').replace(/(\d)([a-zA-Z])/g, '$1 $2').trim(),
          });
        });
      });
    });
    list.sort((a, b) => {
      if (b.year !== a.year) return b.year.localeCompare(a.year);
      return MONTH_IDX.indexOf(b.month) - MONTH_IDX.indexOf(a.month);
    });
    return list;
  }, []);

  // Real-time unread message count
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (!user) return;
      const currentUid = user.uid;
      const unsubscribeMessages = onSnapshot(collectionGroup(db, 'messages'), (snapshot) => {
        const messages = snapshot.docs
          .map(d => {
            const parentColl = d.ref.parent;
            const clientDocRef = parentColl.parent;
            if (!clientDocRef) return null;
            return d.data();
          })
          .filter(Boolean);
        const unread = messages.filter(m =>
          m.read !== true &&
          ['admin', 'internal'].includes(m.recipientType) &&
          m.senderId !== currentUid
        );
        setUnreadMessageCount(unread.length);
      });
      return () => unsubscribeMessages();
    });
    return () => unsubscribeAuth();
  }, []);

  // Real-time activity feed (messages) and recent orders
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const currentUid = user.uid;

    const unsubscribeOrders = onSnapshot(
      collection(db, 'orders'),
      (snapshot) => {
        const allOrders = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const pendingAdminOrders = allOrders.filter(order => order.status === 'Pending Admin Approval');
        const sortedOrders = [...pendingAdminOrders].sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });
        setRecentOrders(sortedOrders.slice(0, 7));
      },
      (error) => { console.error('Error listening to orders:', error); }
    );

    const unsubscribeMessages = onSnapshot(
      collectionGroup(db, 'messages'),
      async (snapshot) => {
        const messages = snapshot.docs
          .map(d => {
            const parentColl = d.ref.parent;
            const clientDocRef = parentColl.parent;
            if (!clientDocRef) return null;
            return { id: d.id, ...d.data(), clientId: clientDocRef.id };
          })
          .filter(Boolean);

        const filteredMessages = messages.filter(m => {
          const recipient = m.recipientType?.toLowerCase?.() ?? '';
          const isInternal = recipient === 'internal' && !m.read;
          const isFromClient = recipient === 'admin' && !m.read;
          return m.senderId !== currentUid && (isInternal || isFromClient);
        });

        const sortedMessages = filteredMessages.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate - aDate;
        });

        const enhancedMessages = await Promise.all(
          sortedMessages.slice(0, 5).map(async (msg) => {
            let clientName = 'Unknown Client';
            let orderInfo = null;
            try {
              const clientDoc = await getDoc(doc(db, 'clients', msg.clientId));
              if (clientDoc.exists()) {
                const d = clientDoc.data();
                clientName = d.name || d.clientName || d.dealershipName || 'Unknown Client';
              }
            } catch (err) { console.error('Error fetching client:', err); }
            if (msg.orderId) {
              try {
                const orderDoc = await getDoc(doc(db, 'orders', msg.orderId));
                if (orderDoc.exists()) {
                  const d = orderDoc.data();
                  orderInfo = {
                    jobNumber: d.jobNumber || 'Unknown',
                    campaignName: d.campaignName || 'Campaign',
                    dealershipName: d.dealershipName || clientName
                  };
                }
              } catch (err) { console.error('Error fetching order:', err); }
            }
            return {
              id: msg.id,
              type: 'message',
              message: msg.text || 'No message content',
              senderName: msg.senderName || 'Unknown',
              timestamp: msg.createdAt?.toDate?.() || new Date(),
              clientId: msg.clientId,
              orderId: msg.orderId,
              clientName,
              orderInfo,
              isOrderMessage: !!msg.orderId,
              icon: '💬'
            };
          })
        );
        setActivities(enhancedMessages);
      },
      (error) => { console.error('Error listening to messages:', error); }
    );

    return () => {
      unsubscribeOrders();
      unsubscribeMessages();
    };
  }, []);

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Fetch dashboard data
  useEffect(() => {
    if (!loaded) return;
    async function fetchData() {
      try {
        const orders = cachedOrders;
        const clients = cachedClients;
        const facebookEvents = cachedFacebookEvents;

        const annSnap = await getDoc(doc(db, 'siteSettings', 'announcements'));

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const parseDate = (value) => {
          if (!value) return null;
          if (value.seconds !== undefined && typeof value.toDate === 'function') return value.toDate();
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [y, m, d] = value.split('-').map(Number);
            return new Date(y, m - 1, d);
          }
          const dt = new Date(value);
          return isNaN(dt) ? null : dt;
        };

        const isLive = (event) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const rawStart = event.liveDate || event.startDate || event.endDate;
          const rawEnd = event.endDate || event.startDate || event.liveDate;
          const start = parseDate(rawStart);
          const end = parseDate(rawEnd);
          if (!start || !end) return false;
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return today >= start && today <= end;
        };

        const currentFacebookEvents = facebookEvents.filter(event => {
          if (event.active === false) return false;
          return isLive(event);
        });

        const checkArtDueSoon = (order) => {
          if (order.status !== 'Incomplete - Need Art') return false;
          const artDueDate = getArtDueDate(order.drops);
          if (!artDueDate) return false;
          const today = new Date();
          const threeDaysFromNow = new Date(today);
          threeDaysFromNow.setDate(today.getDate() + 3);
          return artDueDate <= threeDaysFromNow;
        };

        const artDueSoonOrders = orders.filter(checkArtDueSoon);

        setOrders(orders);
        setClients(clients);
        setAnnouncement(annSnap.exists() ? annSnap.data().text : '');

        setStats(prevStats => ({
          ...prevStats,
          openOrders: orders.filter(o =>
            o.status !== 'Order Complete' && o.status !== 'On Hold' && o.status !== 'Cancelled'
          ).length,
          approvalsClient: orders.filter(o => o.status === 'Pending Client Approval').length,
          approvalsAdmin: orders.filter(o => o.status === 'Pending Admin Approval').length,
          insurancePending: orders.filter(o => o.status === 'Art Complete - No Insurance').length,
          prizeboardPending: orders.filter(o => o.status === 'Art Complete - No Prizeboard').length,
          incompleteArt: orders.filter(o => o.status === 'Incomplete - Need Art').length,
          currentFacebookEvents: currentFacebookEvents.length,
          messages: unreadMessageCount,
          artDueSoon: artDueSoonOrders.length,
        }));
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      }
    }
    fetchData();
  }, [loaded, cachedOrders, cachedClients, cachedFacebookEvents, unreadMessageCount]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({ orders: [], clients: [] });
      return;
    }
    const query = searchQuery.toLowerCase().trim();
    const matchingOrders = orders.filter(order => {
      const jobNumber = (order.jobNumber || order.id || '').toLowerCase();
      const dealershipName = (order.dealershipName || '').toLowerCase();
      const status = (order.status || '').toLowerCase();
      return jobNumber.includes(query) || dealershipName.includes(query) || status.includes(query);
    }).slice(0, 10);
    const matchingClients = clients.filter(client => {
      const name = (client.name || '').toLowerCase();
      const city = (client.city || '').toLowerCase();
      const state = (client.state || '').toLowerCase();
      return name.includes(query) || city.includes(query) || state.includes(query);
    }).slice(0, 10);
    setSearchResults({ orders: matchingOrders, clients: matchingClients });
  }, [searchQuery, orders, clients]);

  const go = section => {
    const statusMap = {
      openOrders: 'Incomplete - Need Art',
      clientApprovals: 'Pending Client Approval',
      adminApprovals: 'Pending Admin Approval',
      insurance: 'Art Complete - No Insurance',
      prizeboard: 'Art Complete - No Prizeboard',
      incompleteArt: 'Incomplete - Need Art',
    };
    const status = statusMap[section];
    if (status) {
      navigate(`/orders?status=${encodeURIComponent(status)}`);
    } else if (section === 'messages') {
      navigate('/messages');
    } else if (section === 'artDueSoon') {
      navigate('/orders?artDueSoon=true');
    }
  };

  const toggleOrderSelect = id => {
    setSelectedOrders(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSearchResultClick = (type, id) => {
    setSearchQuery('');
    if (type === 'order') navigate(`/orders/${id}`);
    else if (type === 'client') navigate(`/clients/${id}`);
  };

  const saveInsuranceNote = async () => {
    if (!insuranceNote.trim()) return;
    try {
      let savedFileUrl = null;
      let savedFileName = null;
      if (insuranceFile) {
        const fileRef = ref(storage, `insurance/saved/${Date.now()}_${insuranceFile.name}`);
        await uploadBytes(fileRef, insuranceFile);
        savedFileUrl = await getDownloadURL(fileRef);
        savedFileName = insuranceFile.name;
      }
      const noteData = {
        note: insuranceNote,
        fileUrl: savedFileUrl,
        fileName: savedFileName,
        createdAt: new Date(),
        createdBy: username || 'Admin'
      };
      await setDoc(doc(db, 'insuranceNotes', Date.now().toString()), noteData);
      const notesSnap = await getDocs(collection(db, 'insuranceNotes'));
      setSavedNotes(notesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      alert('Note and file saved for future use!');
    } catch (err) {
      console.error('Error saving note:', err);
      alert('Failed to save note');
    }
  };

  const deleteInsuranceNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await deleteDoc(doc(db, 'insuranceNotes', noteId));
      const notesSnap = await getDocs(collection(db, 'insuranceNotes'));
      setSavedNotes(notesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      alert('Note deleted successfully!');
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  const loadSavedNote = (note) => {
    setInsuranceNote(note.note);
    if (note.fileUrl && note.fileName) {
      alert(`Note loaded! Original file: ${note.fileName}\nYou can use the same file or upload a new one.`);
    }
  };

  // Sample Vault Functions
  const createNewJob = async () => {
    if (!newJobName.trim()) { alert('Please enter a job name'); return; }
    try {
      const jobData = { name: newJobName.trim(), createdAt: new Date(), createdBy: username || 'Admin', samples: [] };
      await setDoc(doc(db, 'sampleVaultJobs', Date.now().toString()), jobData);
      const jobsSnap = await getDocs(collection(db, 'sampleVaultJobs'));
      const jobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      jobs.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      setSampleVaultJobs(jobs);
      setNewJobName('');
      alert(`Job "${jobData.name}" created successfully!`);
    } catch (err) {
      console.error('Error creating job:', err);
      alert('Failed to create job');
    }
  };

  const uploadSampleToJob = async () => {
    if (!selectedJob) { alert('Please select a job'); return; }
    if (!sampleFile) { alert('Please select a file'); return; }
    if (!sampleFileName.trim()) { alert('Please enter a name for this sample'); return; }
    try {
      const fileRef = ref(storage, `sample-vault/${selectedJob.id}/${Date.now()}_${sampleFile.name}`);
      await uploadBytes(fileRef, sampleFile);
      const fileUrl = await getDownloadURL(fileRef);
      const jobRef = doc(db, 'sampleVaultJobs', selectedJob.id);
      const jobSnap = await getDoc(jobRef);
      const currentSamples = jobSnap.data()?.samples || [];
      const newSample = {
        name: sampleFileName.trim(),
        fileName: sampleFile.name,
        fileUrl,
        uploadedAt: new Date(),
        uploadedBy: username || 'Admin'
      };
      await updateDoc(jobRef, { samples: [...currentSamples, newSample] });
      const jobsSnap = await getDocs(collection(db, 'sampleVaultJobs'));
      const jobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      jobs.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      setSampleVaultJobs(jobs);
      setSelectedJob(jobs.find(j => j.id === selectedJob.id));
      setSampleFile(null);
      setSampleFileName('');
      alert(`Sample "${newSample.name}" uploaded successfully!`);
    } catch (err) {
      console.error('Error uploading sample:', err);
      alert('Failed to upload sample');
    }
  };

  const deleteSampleFromJob = async (jobId, sample) => {
    if (!window.confirm('Are you sure you want to delete this sample?')) return;
    try {
      const jobRef = doc(db, 'sampleVaultJobs', jobId);
      const jobSnap = await getDoc(jobRef);
      const updatedSamples = (jobSnap.data()?.samples || []).filter(s =>
        !(s.name === sample.name && s.fileName === sample.fileName && s.fileUrl === sample.fileUrl)
      );
      await updateDoc(jobRef, { samples: updatedSamples });
      const jobsSnap = await getDocs(collection(db, 'sampleVaultJobs'));
      const jobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      jobs.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      setSampleVaultJobs(jobs);
      if (selectedJob?.id === jobId) setSelectedJob(jobs.find(j => j.id === jobId));
      alert('Sample deleted successfully!');
    } catch (err) {
      console.error('Error deleting sample:', err);
      alert('Failed to delete sample');
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this entire job and all its samples?')) return;
    try {
      await deleteDoc(doc(db, 'sampleVaultJobs', jobId));
      const jobsSnap = await getDocs(collection(db, 'sampleVaultJobs'));
      const jobs = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      jobs.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      setSampleVaultJobs(jobs);
      if (selectedJob?.id === jobId) setSelectedJob(null);
      alert('Job deleted successfully!');
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job');
    }
  };

  const toJSDate = (value) => {
    if (!value) return null;
    if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      const d = new Date(value);
      return isNaN(d) ? null : d;
    }
    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d) ? null : d;
    }
    return null;
  };

  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const formatOneDrop = (drop) => {
    const s = toJSDate(drop?.startDate);
    const e = toJSDate(drop?.endDate);
    if (s && e) return `${fmt.format(s)}–${fmt.format(e)}`;
    if (s && !e) return `starts ${fmt.format(s)}`;
    if (!s && e) return `ends ${fmt.format(e)}`;
    return '-';
  };

  const formatDropDates = (drops) => {
    if (!Array.isArray(drops) || drops.length === 0) return 'No drop dates set';
    return drops.map((d, i) => `Drop ${i + 1}: ${formatOneDrop(d)}`).join('  •  ');
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">{username} Dashboard</h1>
      </div>

      {/* Announcements */}
      {announcement && (
        <div className="announcement-section">
          <div className="announcement-card">
            <p className="announcement-text">{announcement}</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <div className="quick-actions-card">
          <h3 className="section-title">Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn primary" onClick={() => navigate('/orders/new')}>
              <span className="btn-icon">+</span>
              <span className="btn-text">Add Order</span>
            </button>
            <button className="action-btn insurance" onClick={() => setShowInsuranceModal(true)}>
              <span className="btn-icon">🛡️</span>
              <span className="btn-text">Link Insurance</span>
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/clients/new')}>
              <span className="btn-icon">👤</span>
              <span className="btn-text">Add Client</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="search-bar-section">
            <div className="dashboard-search-wrapper">
              <SearchBar
                placeholder="🔍 Search anything..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery.trim() && (searchResults.orders.length > 0 || searchResults.clients.length > 0) && (
                <div className="search-dropdown">
                  {searchResults.orders.length > 0 && (
                    <div className="dropdown-section">
                      <h4 className="dropdown-title">
                        <span className="dropdown-icon">📋</span>
                        Orders ({searchResults.orders.length})
                      </h4>
                      <div className="dropdown-list">
                        {searchResults.orders.map(order => (
                          <div key={order.id} className="dropdown-item" onClick={() => handleSearchResultClick('order', order.id)}>
                            <div className="dropdown-main">
                              <span className="dropdown-title-text">#{order.jobNumber || order.id}</span>
                              <span className="dropdown-subtitle">{order.dealershipName || 'Unknown Dealership'}</span>
                            </div>
                            <span className={`dropdown-status ${(order.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              {order.status || 'Unknown Status'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchResults.clients.length > 0 && (
                    <div className="dropdown-section">
                      <h4 className="dropdown-title">
                        <span className="dropdown-icon">👤</span>
                        Clients ({searchResults.clients.length})
                      </h4>
                      <div className="dropdown-list">
                        {searchResults.clients.map(client => (
                          <div key={client.id} className="dropdown-item" onClick={() => handleSearchResultClick('client', client.id)}>
                            <div className="dropdown-main">
                              <span className="dropdown-title-text">{client.name || 'Unnamed Client'}</span>
                              <span className="dropdown-subtitle">
                                {client.city && client.state
                                  ? `${client.city}, ${client.state}`
                                  : client.city || client.state || 'Location not specified'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {searchQuery.trim() && searchResults.orders.length === 0 && searchResults.clients.length === 0 && (
                <div className="search-dropdown">
                  <div className="dropdown-no-results">
                    <span className="no-results-icon">🔍</span>
                    <p>No results found for "{searchQuery}"</p>
                    <small>Try searching by job number, dealership, client name, or location</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Stats Cards */}
      <div className="stats-section">
        <h3 className="section-title">Dashboard Overview</h3>
        <div className="stats-grid">
          <DashboardCard title="Open Orders" value={stats.openOrders} onClick={() => go('openOrders')} />
          <DashboardCard title="Client Approvals" value={stats.approvalsClient} onClick={() => go('clientApprovals')} />
          <DashboardCard title="Admin Approvals" value={stats.approvalsAdmin} onClick={() => go('adminApprovals')} />
          <DashboardCard title="Insurance Pending" value={stats.insurancePending} onClick={() => go('insurance')} />
          <DashboardCard title="Prizeboard Pending" value={stats.prizeboardPending} onClick={() => go('prizeboard')} />
          <DashboardCard title="Incomplete Art" value={stats.incompleteArt} onClick={() => go('incompleteArt')} />
          <DashboardCard title="Current Facebook Events" value={stats.currentFacebookEvents} onClick={() => navigate('/facebook-events?filter=current')} />
          <DashboardCard title="Art Incomplete - Due Soon" value={stats.artDueSoon} onClick={() => go('artDueSoon')} />
        </div>
      </div>

      {/* Activity Feed and Recent Orders Row */}
      <div className="activity-recent-row">
        {/* Activity Feed */}
        <div className="activity-feed-section">
          <div className="calendar-card">
            <h3 className="section-title">
              <span className="section-icon">💬</span>
              Unread Messages
            </h3>
            <div className="activity-list">
              {activities.length === 0 ? (
                <div className="no-activity">
                  <span className="no-activity-icon">✅</span>
                  <p>No unread messages</p>
                </div>
              ) : (
                activities.map(activity => (
                  <div
                    key={activity.id}
                    className="activity-item"
                    onClick={() => {
                      if (activity.orderId) navigate(`/orders/${activity.orderId}`);
                      else if (activity.clientId) navigate(`/clients/${activity.clientId}`);
                    }}
                    style={{ cursor: (activity.orderId || activity.clientId) ? 'pointer' : 'default' }}
                  >
                    <div className="activity-icon">{activity.icon}</div>
                    <div className="activity-content">
                      <p className="activity-sender">{activity.senderName}</p>
                      <p className="activity-context">
                        {activity.isOrderMessage && activity.orderInfo ? (
                          <>{activity.orderInfo.jobNumber} - {activity.orderInfo.campaignName} - {activity.orderInfo.dealershipName}</>
                        ) : (
                          activity.clientName
                        )}
                      </p>
                      <p className="activity-message">{activity.message}</p>
                      <small className="activity-time">{formatActivityTime(activity.timestamp)}</small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="recent-orders-section">
          <div className="calendar-card">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
              <h3 className="section-title" style={{ margin: 0 }}>
                <span className="section-icon">⏳</span>
                Pending Admin Approval
              </h3>
            </div>
            <div className="recent-orders-list">
              {recentOrders.length === 0 ? (
                <div className="no-orders-placeholder">
                  <span className="no-orders-icon">✅</span>
                  <p>No orders pending admin approval</p>
                </div>
              ) : (
                recentOrders.map(order => (
                  <div
                    key={order.id}
                    className="recent-order-item"
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <div className="order-left">
                      <span className="order-status-indicator yellow"></span>
                      <div className="order-details">
                        <span className="order-job-number">#{(order.jobNumber || order.id).toString().trim()}</span>
                        <span className="order-dealership">{order.dealershipName || 'Unknown Dealership'}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Facebook Ads Carousel + Sample Vault- side by side */}
      <div style={{ display: 'flex', gap: '24px', marginTop: '40px', alignItems: 'flex-start' }}>

      {/* Facebook Ads Carousel */}
      <div style={{ flex: '1 1 0', minWidth: 0 }} className="calendar-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 className="section-title" style={{ margin: 0 }}>
              <span className="section-icon">📸</span>
              Recent Facebook Ads
            </h3>
            {facebookAds.length > 0 && (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {currentAdIndex + 1} / {facebookAds.length}
              </span>
            )}
          </div>

          {adLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <div style={{
                width: '40px', height: '40px', border: '3px solid #e5e7eb',
                borderTop: '3px solid #3b82f6', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
              }} />
              <p style={{ fontSize: '14px', margin: 0 }}>Loading ads...</p>
            </div>
          ) : facebookAds.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>🖼️</span>
              <p style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 6px' }}>No ads found</p>
              <p style={{ fontSize: '13px', margin: 0 }}>Upload Facebook ad templates to see them here</p>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Main image display */}
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#f1f5f9',
                aspectRatio: '1 / 1',
              }}>
                <img
                  key={currentAdIndex}
                  src={facebookAds[currentAdIndex]?.url}
                  alt={`Facebook ad ${currentAdIndex + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                    animation: 'fadeIn 0.4s ease',
                  }}
                  onError={e => { e.target.style.display = 'none'; }}
                />

                {/* Left / Right arrows */}
                {facebookAds.length > 1 && (
                  <>
                    <button
                      onClick={() => {
                        clearInterval(adIntervalRef.current);
                        setCurrentAdIndex(prev => (prev - 1 + facebookAds.length) % facebookAds.length);
                        adIntervalRef.current = setInterval(() => {
                          setCurrentAdIndex(p => (p + 1) % facebookAds.length);
                        }, 4000);
                      }}
                      style={{
                        position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.45)', color: 'white', border: 'none',
                        borderRadius: '50%', width: '36px', height: '36px', fontSize: '18px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
                    >‹</button>
                    <button
                      onClick={() => {
                        clearInterval(adIntervalRef.current);
                        setCurrentAdIndex(prev => (prev + 1) % facebookAds.length);
                        adIntervalRef.current = setInterval(() => {
                          setCurrentAdIndex(p => (p + 1) % facebookAds.length);
                        }, 4000);
                      }}
                      style={{
                        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.45)', color: 'white', border: 'none',
                        borderRadius: '50%', width: '36px', height: '36px', fontSize: '18px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
                    >›</button>
                  </>
                )}
              </div>

              {/* Dot indicators */}
              {facebookAds.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '14px', flexWrap: 'wrap' }}>
                  {facebookAds.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        clearInterval(adIntervalRef.current);
                        setCurrentAdIndex(i);
                        adIntervalRef.current = setInterval(() => {
                          setCurrentAdIndex(p => (p + 1) % facebookAds.length);
                        }, 4000);
                      }}
                      style={{
                        width: i === currentAdIndex ? '22px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        background: i === currentAdIndex ? '#3b82f6' : '#d1d5db',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'all 0.25s ease',
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Ad label + Download button */}
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {facebookAds[currentAdIndex] && (
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>
                    {facebookAds[currentAdIndex].adType
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/([a-zA-Z])(\d)/g, '$1 $2')
                      .replace(/(\d)([a-zA-Z])/g, '$1 $2')
                      .trim()}- {facebookAds[currentAdIndex].month} {facebookAds[currentAdIndex].year}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button
                    className="action-btn primary"
                    onClick={() => {
                      clearInterval(adIntervalRef.current);
                      setDownloadAdInfo(facebookAds[currentAdIndex]);
                      setShowDownloadAdModal(true);
                    }}
                    disabled={facebookAds.length === 0 || adLoading}
                    style={{ fontSize: '13px', padding: '8px 18px' }}
                  >
                    <span className="btn-icon">⬇️</span>
                    <span className="btn-text">Download Ad</span>
                  </button>
                  <button
                    className="action-btn secondary"
                    onClick={() => {
                      clearInterval(adIntervalRef.current);
                      setShowSearchAdsModal(true);
                    }}
                    style={{ fontSize: '13px', padding: '8px 18px' }}
                  >
                    <span className="btn-icon">🔍</span>
                    <span className="btn-text">Search Ads</span>
                  </button>
                  <button
                    className="action-btn secondary"
                    onClick={() => {
                      clearInterval(adIntervalRef.current);
                      setShowVerbiageModal(true);
                    }}
                    style={{ fontSize: '13px', padding: '8px 18px' }}
                  >
                    <span className="btn-icon">📝</span>
                    <span className="btn-text">Verbiage</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Sample Vault Section */}
      <div style={{ flex: '1 1 0', minWidth: 0 }} className="calendar-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 className="section-title" style={{ margin: 0 }}>Sample Vault</h3>
            <button
              className="action-btn primary"
              onClick={() => { setVaultModalMode('full'); setSelectedJob(null); setShowSampleVaultModal(true); }}
              style={{ fontSize: '14px', padding: '10px 20px' }}
            >
              <span className="btn-icon">📂</span>
              <span className="btn-text">Manage Samples</span>
            </button>
          </div>

          {sampleVaultJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📁</span>
              <p style={{ fontSize: '16px', margin: '0 0 8px 0', fontWeight: 600 }}>No sample jobs yet</p>
              <p style={{ fontSize: '14px', margin: 0 }}>Click "Manage Samples" to create your first job</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
              {sampleVaultJobs.map(job => (
                <div
                  key={job.id}
                  style={{
                    background: '#f8fafc', padding: '16px', borderRadius: '8px',
                    border: '1px solid #e5e7eb', cursor: 'pointer',
                    transition: 'all 0.2s', textAlign: 'center'
                  }}
                  onClick={() => { setSelectedJob(job); setVaultModalMode('viewOnly'); setShowSampleVaultModal(true); }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>{job.name}</h4>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                    {job.samples?.length || 0} sample{(job.samples?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Sample Vault Modal */}
      {showSampleVaultModal && (
        <div className="modal-overlay">
          <div className="insurance-modal" style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3 className="modal-title">
                <span className="modal-icon">📂</span>
                {vaultModalMode === 'viewOnly' ? (selectedJob?.name || 'Sample Vault') : 'Sample Vault Manager'}
              </h3>
              <button
                className="modal-close"
                onClick={() => { setShowSampleVaultModal(false); setSelectedJob(null); setNewJobName(''); setSampleFile(null); setSampleFileName(''); setVaultModalMode('full'); }}
                aria-label="Close modal"
              >×</button>
            </div>

            <div className="modal-content">
              {vaultModalMode === 'viewOnly' ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#1f2937' }}>🖼️ Samples</h4>
                    <button
                      onClick={() => setVaultModalMode('full')}
                      style={{ padding: '6px 12px', background: '#eff6ff', color: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = 'white'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b82f6'; }}
                    >Manage All Jobs</button>
                  </div>
                  <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#374151' }}>Upload Sample</label>
                    <input type="text" value={sampleFileName} onChange={e => setSampleFileName(e.target.value)} placeholder="Sample name (e.g., Ford Manufacturer)" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }} />
                    <input type="file" accept="image/*,application/pdf" onChange={e => setSampleFile(e.target.files[0])} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }} />
                    <button onClick={uploadSampleToJob} disabled={!sampleFile || !sampleFileName.trim()} style={{ width: '100%', padding: '10px', background: (sampleFile && sampleFileName.trim()) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#d1d5db', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: (sampleFile && sampleFileName.trim()) ? 'pointer' : 'not-allowed' }}>Upload Sample</button>
                  </div>
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {!selectedJob?.samples || selectedJob.samples.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}><p style={{ fontSize: '14px', margin: 0 }}>No samples uploaded yet</p></div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[...selectedJob.samples].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())).map((sample, index) => (
                          <div key={index} style={{ padding: '12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <h5 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{sample.name}</h5>
                              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>📎 {sample.fileName}</p>
                              <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>Uploaded {sample.uploadedAt?.toDate?.().toLocaleDateString() || 'Unknown'}</p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => window.open(sample.fileUrl, '_blank')} style={{ padding: '6px 12px', background: '#eff6ff', color: '#3b82f6', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'} onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}>View</button>
                              <button onClick={() => downloadFile(sample.fileUrl, sample.fileName)} style={{ padding: '6px 12px', background: '#f0fdf4', color: '#10b981', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'} onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}>Download</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1f2937' }}>📋 Jobs</h4>
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#374151' }}>Create New Job</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input type="text" value={newJobName} onChange={e => setNewJobName(e.target.value)} placeholder="e.g., Blowout Event" style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }} />
                        <button onClick={createNewJob} disabled={!newJobName.trim()} style={{ padding: '8px 16px', background: newJobName.trim() ? 'linear-gradient(135deg, #10b981, #059669)' : '#d1d5db', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: newJobName.trim() ? 'pointer' : 'not-allowed' }}>Create</button>
                      </div>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {sampleVaultJobs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}><p style={{ fontSize: '14px', margin: 0 }}>No jobs created yet</p></div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {sampleVaultJobs.map(job => (
                            <div key={job.id} style={{ padding: '12px', background: selectedJob?.id === job.id ? '#eff6ff' : 'white', border: `2px solid ${selectedJob?.id === job.id ? '#3b82f6' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => setSelectedJob(job)}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div style={{ flex: 1 }}>
                                  <h5 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{job.name}</h5>
                                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{job.samples?.length || 0} sample{(job.samples?.length || 0) !== 1 ? 's' : ''}</p>
                                </div>
                                <button onClick={e => { e.stopPropagation(); deleteJob(job.id); }} style={{ padding: '4px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#fecaca'} onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}>Delete</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#1f2937' }}>🖼️ Samples {selectedJob && `- ${selectedJob.name}`}</h4>
                    {!selectedJob ? (
                      <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>👈</span>
                        <p style={{ fontSize: '14px', margin: 0 }}>Select a job to manage samples</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#374151' }}>Upload Sample</label>
                          <input type="text" value={sampleFileName} onChange={e => setSampleFileName(e.target.value)} placeholder="Sample name (e.g., Ford Manufacturer)" style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }} />
                          <input type="file" accept="image/*,application/pdf" onChange={e => setSampleFile(e.target.files[0])} style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }} />
                          <button onClick={uploadSampleToJob} disabled={!sampleFile || !sampleFileName.trim()} style={{ width: '100%', padding: '10px', background: (sampleFile && sampleFileName.trim()) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#d1d5db', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: (sampleFile && sampleFileName.trim()) ? 'pointer' : 'not-allowed' }}>Upload Sample</button>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {!selectedJob.samples || selectedJob.samples.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}><p style={{ fontSize: '14px', margin: 0 }}>No samples uploaded yet</p></div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {[...selectedJob.samples].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())).map((sample, index) => (
                                <div key={index} style={{ padding: '12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ flex: 1 }}>
                                    <h5 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{sample.name}</h5>
                                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>📎 {sample.fileName}</p>
                                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>Uploaded {sample.uploadedAt?.toDate?.().toLocaleDateString() || 'Unknown'}</p>
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={() => window.open(sample.fileUrl, '_blank')} style={{ padding: '6px 12px', background: '#eff6ff', color: '#3b82f6', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'} onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}>View</button>
                                    <button onClick={() => downloadFile(sample.fileUrl, sample.fileName)} style={{ padding: '6px 12px', background: '#f0fdf4', color: '#10b981', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'} onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}>Download</button>
                                    <button onClick={() => deleteSampleFromJob(selectedJob.id, sample)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#fecaca'} onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}>Delete</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowSampleVaultModal(false); setSelectedJob(null); setNewJobName(''); setSampleFile(null); setSampleFileName(''); setVaultModalMode('full'); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Insurance Modal */}
      {showInsuranceModal && (
        <div className="modal-overlay">
          <div className="insurance-modal">
            <div className="modal-header">
              <h3 className="modal-title">
                <span className="modal-icon">🛡️</span>
                Link Insurance Policy to Orders
              </h3>
              <button className="modal-close" onClick={() => setShowInsuranceModal(false)} aria-label="Close modal">×</button>
            </div>

            <div className="modal-content">
              <div className="upload-section">
                <label className="upload-label">
                  <span className="label-icon">📁</span>
                  Upload Insurance File
                </label>
                <div className="file-input-wrapper">
                  <input type="file" onChange={e => setInsuranceFile(e.target.files[0])} className="file-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                  <div className="file-input-display">
                    {insuranceFile ? (
                      <div className="file-selected">
                        <span className="file-icon">📄</span>
                        <span className="file-name">{insuranceFile.name}</span>
                        <span className="file-size">({(insuranceFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                    ) : (
                      <div className="file-placeholder">
                        <span className="placeholder-icon">📁</span>
                        <span>Choose file or drag and drop</span>
                        <small>PDF, DOC, DOCX, JPG, PNG (Max 10MB)</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="insurance-notes-section">
                <label className="upload-label">
                  <span className="label-icon">📝</span>
                  Insurance Policy Notes
                </label>
                <p className="notes-description">List the dealerships that this insurance policy covers (in case there's no order made for them yet):</p>
                <textarea className="insurance-notes-textarea" placeholder="Enter dealership names covered by this insurance policy..." value={insuranceNote} onChange={e => setInsuranceNote(e.target.value)} rows={4} />
                <div className="save-future-section">
                  <label className="save-future-checkbox">
                    <input type="checkbox" checked={saveForFuture} onChange={e => setSaveForFuture(e.target.checked)} />
                    <span className="checkbox-custom"></span>
                    <span>Save For Future Uploads?</span>
                  </label>
                  {saveForFuture && (
                    <button className="btn-save-note" onClick={saveInsuranceNote} disabled={!insuranceNote.trim()}>Save Note</button>
                  )}
                </div>
                {savedNotes.length > 0 && (
                  <div className="saved-notes-section">
                    <h4 className="saved-notes-title"><span className="title-icon">💾</span> Previously Saved Notes</h4>
                    <div className="saved-notes-list">
                      {savedNotes.map(note => (
                        <div key={note.id} className="saved-note-item">
                          <div className="saved-note-content">
                            <p className="saved-note-text">{note.note}</p>
                            {note.fileName && <p className="saved-file-info">📁 File: {note.fileName}</p>}
                            <small className="saved-note-meta">Saved by {note.createdBy} on {note.createdAt?.toDate?.().toLocaleDateString() || 'Unknown date'}</small>
                          </div>
                          <div className="saved-note-actions">
                            <button className="btn-use-note" onClick={() => loadSavedNote(note)} title="Use this note">📋</button>
                            {note.fileUrl && <button className="btn-download-file" onClick={() => window.open(note.fileUrl, '_blank')} title="Download saved file">📥</button>}
                            <button className="btn-delete-note" onClick={() => deleteInsuranceNote(note.id)} title="Delete this note">🗑️</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="orders-selection">
                <h4 className="selection-title"><span className="title-icon">📋</span> Select Orders to Link</h4>
                <div className="orders-list">
                  {orders
                    .filter(order => {
                      const status = order.status || '';
                      const excludedStatuses = ['Order Complete', 'Cancelled', 'Art Complete - No Prizeboard', 'Pending Prizeboard Approval'];
                      return !excludedStatuses.includes(status);
                    })
                    .map(order => (
                      <label key={order.id} className="order-checkbox">
                        <input type="checkbox" checked={selectedOrders.includes(order.id)} onChange={() => toggleOrderSelect(order.id)} className="checkbox-input" />
                        <span className="checkbox-custom"></span>
                        <div className="order-info">
                          <span className="order-number">#{order.jobNumber || order.id}</span>
                          <span className="order-dealer">{order.dealershipName || 'Unknown Dealership'}</span>
                          <span className="order-dates">{formatDropDates(order.drops)}</span>
                        </div>
                      </label>
                    ))}
                </div>
                {orders.filter(order => {
                  const status = order.status || '';
                  return !['Order Complete', 'Cancelled', 'Art Complete - No Prizeboard', 'Pending Prizeboard Approval'].includes(status);
                }).length === 0 && (
                  <div className="no-orders">
                    <span className="no-orders-icon">🔭</span>
                    <p>No orders available to link insurance</p>
                  </div>
                )}
              </div>

              {selectedOrders.length > 0 && (
                <div className="selection-summary">
                  <div className="summary-content">
                    <span className="summary-icon">✅</span>
                    <span className="summary-text">{selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected</span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowInsuranceModal(false)}>Cancel</button>
              <button
                className="btn-link-insurance"
                disabled={!insuranceFile || selectedOrders.length === 0}
                onClick={async (e) => {
                  if (!insuranceFile) return alert('Please select a file');
                  if (selectedOrders.length === 0) return alert('Please select at least one order');
                  const button = e.target;
                  const originalText = button.textContent;
                  try {
                    button.textContent = 'Linking...';
                    button.disabled = true;
                    const fileRef = ref(storage, `insurance/${Date.now()}_${insuranceFile.name}`);
                    await uploadBytes(fileRef, insuranceFile);
                    const fileUrl = await getDownloadURL(fileRef);
                    await Promise.all(selectedOrders.map(async (orderId) => {
                      const updateData = { insuranceFile: fileUrl, insuranceLinked: true };
                      if (insuranceNote.trim()) updateData.insuranceNote = insuranceNote;
                      await updateDoc(doc(db, 'orders', orderId), updateData);
                    }));
                    refresh(['orders']);
                    alert(`Insurance linked successfully to ${selectedOrders.length} order${selectedOrders.length !== 1 ? 's' : ''}!`);
                    setShowInsuranceModal(false);
                    setSelectedOrders([]);
                    setInsuranceFile(null);
                    setInsuranceNote('');
                    setSaveForFuture(false);
                  } catch (err) {
                    console.error('Upload failed:', err);
                    alert('Something went wrong while uploading. Please try again.');
                    button.textContent = originalText;
                    button.disabled = false;
                  }
                }}
              >
                <span className="btn-icon">🔗</span>
                Link Insurance{selectedOrders.length > 0 && ` (${selectedOrders.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Shared client picker helper ── rendered inline in both modals */}
      {/* Download Ad Modal (from carousel) */}
      {showDownloadAdModal && (() => {
        const ad = downloadAdInfo;
        const filtered = [...clients]
          .filter(c => c.manufacturer && (c.name || '').toLowerCase().includes(downloadClientSearch.toLowerCase()))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const allIds = filtered.map(c => c.id);
        return (
          <div className="modal-overlay">
            <div className="insurance-modal" style={{ maxWidth: '540px' }}>
              <div className="modal-header">
                <h3 className="modal-title"><span className="modal-icon">⬇️</span> Download Ad</h3>
                <button className="modal-close" onClick={closeDownloadModal} aria-label="Close modal">×</button>
              </div>
              <div className="modal-content">
                {ad && (
                  <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '14px', color: '#0369a1' }}>
                    <strong>{ad.adType.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([a-zA-Z])(\d)/g, '$1 $2').replace(/(\d)([a-zA-Z])/g, '$1 $2').trim()}</strong>
                    {'- '}{ad.month} {ad.year}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Select Dealerships</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setDownloadSelectedClients(new Set(allIds))} style={{ fontSize: '12px', padding: '3px 8px', background: '#eff6ff', color: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '4px', cursor: 'pointer' }}>Select All</button>
                    <button onClick={() => setDownloadSelectedClients(new Set())} style={{ fontSize: '12px', padding: '3px 8px', background: '#f9fafb', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Clear</button>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Search dealerships..."
                  value={downloadClientSearch}
                  onChange={e => setDownloadClientSearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                />
                <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
         
                  {filtered.length === 0 ? (
                    <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>No dealerships found</p>
                  ) : filtered.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: downloadSelectedClients.has(c.id) ? '#eff6ff' : 'white' }}>
                      <input
                        type="checkbox"
                        checked={downloadSelectedClients.has(c.id)}
                        onChange={() => {
                          const next = new Set(downloadSelectedClients);
                          next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                          setDownloadSelectedClients(next);
                        }}
                      />
                      <span style={{ flex: 1, fontSize: '14px', color: '#1f2937' }}>{c.name}</span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>{c.manufacturer}</span>
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                  {downloadSelectedClients.size} selected · Dealerships without a manufacturer are hidden
                </p>
                {downloadingAd && downloadProgress.total > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{ background: '#3b82f6', height: '100%', width: `${(downloadProgress.current / downloadProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                    </div>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>
                      Generating {downloadProgress.current} / {downloadProgress.total}...
                    </p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={closeDownloadModal} disabled={downloadingAd}>Cancel</button>
                <button className="btn-link-insurance" disabled={downloadSelectedClients.size === 0 || downloadingAd} onClick={() => handleDownloadForClients(ad)}>
                  {downloadingAd ? `Generating ${downloadProgress.current}/${downloadProgress.total}...` : <><span className="btn-icon">⬇️</span> Download ({downloadSelectedClients.size})</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Search Ads Modal */}
      {showSearchAdsModal && (() => {
        const filteredAds = adSearchQuery.trim()
          ? ALL_AD_TYPES.filter(a =>
              a.displayName.toLowerCase().includes(adSearchQuery.toLowerCase()) ||
              a.month.toLowerCase().includes(adSearchQuery.toLowerCase()) ||
              a.year.includes(adSearchQuery)
            )
          : ALL_AD_TYPES;
        const filtered = [...clients]
          .filter(c => c.manufacturer && (c.name || '').toLowerCase().includes(downloadClientSearch.toLowerCase()))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const allIds = filtered.map(c => c.id);
        return (
          <div className="modal-overlay">
            <div className="insurance-modal" style={{ maxWidth: '760px' }}>
              <div className="modal-header">
                <h3 className="modal-title"><span className="modal-icon">🔍</span> Search & Download Ads</h3>
                <button className="modal-close" onClick={closeSearchAdsModal} aria-label="Close modal">×</button>
              </div>
              <div className="modal-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Search Ads</label>
                  <input
                    type="text"
                    placeholder="Search by name, month, or year..."
                    value={adSearchQuery}
                    onChange={e => setAdSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                    autoFocus
                  />
                  <div style={{ maxHeight: '360px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    {filteredAds.length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>No ads found</p>
                    ) : filteredAds.map((ad, i) => (
                      <div
                        key={i}
                        onClick={() => setSearchSelectedAd(ad)}
                        style={{
                          padding: '9px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                          background: searchSelectedAd && searchSelectedAd.adType === ad.adType && searchSelectedAd.month === ad.month && searchSelectedAd.year === ad.year ? '#eff6ff' : 'white',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}
                      >
                        <span style={{ fontSize: '13px', color: '#1f2937', fontWeight: searchSelectedAd && searchSelectedAd.adType === ad.adType && searchSelectedAd.month === ad.month ? 600 : 400 }}>{ad.displayName}</span>
                        <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: '8px' }}>{ad.month} {ad.year}</span>
                      </div>
                    ))}
                  </div>
                  {searchSelectedAd && (
                    <div style={{ marginTop: '10px', background: '#f0f9ff', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#0369a1' }}>
                      ✓ Selected: <strong>{searchSelectedAd.displayName}</strong>- {searchSelectedAd.month} {searchSelectedAd.year}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Select Dealerships</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setDownloadSelectedClients(new Set(allIds))} style={{ fontSize: '11px', padding: '2px 7px', background: '#eff6ff', color: '#3b82f6', border: '1px solid #3b82f6', borderRadius: '4px', cursor: 'pointer' }}>All</button>
                      <button onClick={() => setDownloadSelectedClients(new Set())} style={{ fontSize: '11px', padding: '2px 7px', background: '#f9fafb', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}>Clear</button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search dealerships..."
                    value={downloadClientSearch}
                    onChange={e => setDownloadClientSearch(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    {filtered.length === 0 ? (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>No dealerships found</p>
                    ) : filtered.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: downloadSelectedClients.has(c.id) ? '#eff6ff' : 'white' }}>
                        <input
                          type="checkbox"
                          checked={downloadSelectedClients.has(c.id)}
                          onChange={() => {
                            const next = new Set(downloadSelectedClients);
                            next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                            setDownloadSelectedClients(next);
                          }}
                        />
                        <span style={{ flex: 1, fontSize: '14px', color: '#1f2937' }}>{c.name}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{c.manufacturer}</span>
                      </label>
                    ))}
                  </div>
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                    {downloadSelectedClients.size} selected · Dealerships without a manufacturer are hidden
                  </p>
                  {downloadingAd && downloadProgress.total > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ background: '#3b82f6', height: '100%', width: `${(downloadProgress.current / downloadProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                      </div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', textAlign: 'center' }}>
                        Generating {downloadProgress.current} / {downloadProgress.total}...
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={closeSearchAdsModal} disabled={downloadingAd}>Cancel</button>
                <button
                  className="btn-link-insurance"
                  disabled={!searchSelectedAd || downloadSelectedClients.size === 0 || downloadingAd}
                  onClick={() => handleDownloadForClients(searchSelectedAd)}
                >
                  {downloadingAd
                    ? `Generating ${downloadProgress.current}/${downloadProgress.total}...`
                    : <><span className="btn-icon">⬇️</span> Download {searchSelectedAd ? `"${searchSelectedAd.displayName}"` : ''} ({downloadSelectedClients.size} dealership{downloadSelectedClients.size !== 1 ? 's' : ''})</>
                  }
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Verbiage Modal */}
      {showVerbiageModal && (() => {
        const templateNames = Object.keys(VERBIAGE_TEMPLATES).sort((a, b) => a.localeCompare(b));
        const filteredTemplates = verbiageTemplateSearch.trim()
          ? templateNames.filter(n => n.toLowerCase().includes(verbiageTemplateSearch.toLowerCase()))
          : templateNames;
        const filteredClients = [...clients]
          .filter(c => (c.name || '').toLowerCase().includes(verbiageClientSearch.toLowerCase()))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        const selectedClient = clients.find(c => c.id === selectedVerbiageClientId);
        return (
          <div className="modal-overlay">
            <div className="insurance-modal" style={{ maxWidth: '820px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header">
                <h3 className="modal-title"><span className="modal-icon">📝</span> Facebook Verbiage</h3>
                <button className="modal-close" onClick={closeVerbiageModal} aria-label="Close modal">×</button>
              </div>
              <div className="modal-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', flex: 1, minHeight: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Select Verbiage Type</label>
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={verbiageTemplateSearch}
                    onChange={e => setVerbiageTemplateSearch(e.target.value)}
                    autoFocus
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '320px' }}>
                    {filteredTemplates.map(name => (
                      <div
                        key={name}
                        onClick={() => {
                          setSelectedVerbiageTemplate(name);
                          setGeneratedVerbiage('');
                          setVerbiageCopied(false);
                          if (selectedVerbiageClientId) generateVerbiageForClient(name, selectedVerbiageClientId);
                        }}
                        style={{
                          padding: '9px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '13px',
                          background: selectedVerbiageTemplate === name ? '#eff6ff' : 'white',
                          color: selectedVerbiageTemplate === name ? '#1d4ed8' : '#1f2937',
                          fontWeight: selectedVerbiageTemplate === name ? 600 : 400,
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Select Dealership</label>
                  <input
                    type="text"
                    placeholder="Search dealerships..."
                    value={verbiageClientSearch}
                    onChange={e => setVerbiageClientSearch(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', marginBottom: '12px' }}>
                    {filteredClients.map(c => (
                      <div
                        key={c.id}
                        onClick={() => {
                          setSelectedVerbiageClientId(c.id);
                          setGeneratedVerbiage('');
                          setVerbiageCopied(false);
                          if (selectedVerbiageTemplate) generateVerbiageForClient(selectedVerbiageTemplate, c.id);
                        }}
                        style={{
                          padding: '8px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '13px',
                          background: selectedVerbiageClientId === c.id ? '#eff6ff' : 'white',
                          color: selectedVerbiageClientId === c.id ? '#1d4ed8' : '#1f2937',
                          fontWeight: selectedVerbiageClientId === c.id ? 600 : 400,
                          display: 'flex', justifyContent: 'space-between'
                        }}
                      >
                        <span>{c.name}</span>
                        {c.city && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{c.city}, {c.state}</span>}
                      </div>
                    ))}
                  </div>
                  {generatingVerbiage && (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '14px' }}>
                      Generating...
                    </div>
                  )}
                  {!generatingVerbiage && generatedVerbiage && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                          {selectedClient ? selectedClient.name : ''}- {selectedVerbiageTemplate}
                        </span>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(generatedVerbiage);
                              setVerbiageCopied(true);
                              setTimeout(() => setVerbiageCopied(false), 2000);
                            } catch (e) { alert('Copy failed'); }
                          }}
                          style={{
                            padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            background: verbiageCopied ? '#10b981' : '#3b82f6', color: 'white', border: 'none',
                            transition: 'background 0.2s'
                          }}
                        >
                          {verbiageCopied ? '✓ Copied!' : 'Copy'}
                        </button>
                      </div>
                      <textarea
                        readOnly
                        value={generatedVerbiage}
                        style={{
                          flex: 1, minHeight: '120px', maxHeight: '160px', width: '100%', resize: 'vertical',
                          padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
                          fontSize: '12px', lineHeight: '1.5', color: '#1f2937', background: '#f9fafb',
                          fontFamily: 'monospace', boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                  {!generatingVerbiage && !generatedVerbiage && (
                    <div style={{ color: '#d1d5db', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>
                      {!selectedVerbiageTemplate && !selectedVerbiageClientId ? 'Select a template and dealership' : !selectedVerbiageTemplate ? 'Select a template' : 'Select a dealership'}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={closeVerbiageModal}>Close</button>
                {generatedVerbiage && (
                  <button
                    className="btn-link-insurance"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(generatedVerbiage);
                        setVerbiageCopied(true);
                        setTimeout(() => setVerbiageCopied(false), 2000);
                      } catch (e) { alert('Copy failed'); }
                    }}
                  >
                    {verbiageCopied ? '✓ Copied!' : <><span className="btn-icon">📋</span> Copy to Clipboard</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Keyframe styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}